/* BackendDownScreen — full-screen, on-brand "We'll be right back" overlay.
 *
 * Shows whenever the backend looks unreachable (see lib/backendStatus.js).
 * While visible it quietly polls /api/health every few seconds; the first
 * successful response flips the app back to normal automatically, so the
 * screen disappears the moment the backend recovers — no manual refresh
 * needed (though a "Try again" button is provided for impatient users).
 */
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import api from "../lib/api";
import { subscribeBackendStatus, reportBackendUp } from "../lib/backendStatus";
import { VerqoMark } from "./VerqoMark";

export default function BackendDownScreen() {
  const [down, setDown] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => subscribeBackendStatus(setDown), []);

  const ping = async () => {
    setChecking(true);
    try {
      await api.get("/health", { timeout: 8000 });
      reportBackendUp(); // success also flips status via the interceptor
    } catch {
      /* still down — keep waiting */
    } finally {
      setChecking(false);
    }
  };

  // Auto-retry on a gentle cadence while the screen is up.
  useEffect(() => {
    if (!down) return undefined;
    const id = setInterval(ping, 5000);
    ping();
    return () => clearInterval(id);
  }, [down]);

  if (!down) return null;

  return (
    <div
      data-testid="backend-down-screen"
      className="fixed inset-0 z-[9999] bg-[#f6f6f0] text-[#0f1410] grid place-items-center px-6"
    >
      <div className="max-w-md w-full text-center">
        {/* Logo — favicon-matching brand mark */}
        <div className="inline-flex items-center gap-2.5 mb-10">
          <VerqoMark className="w-10 h-10 rounded-xl overflow-hidden"/>
          <span className="text-base font-semibold tracking-tight">verqo</span>
        </div>

        {/* Animated pulse */}
        <div className="relative mx-auto mb-8 w-14 h-14">
          <span className="absolute inset-0 rounded-full bg-[#c5f955]/30 animate-ping" />
          <span className="absolute inset-2 rounded-full bg-[#c5f955]/70" />
          <span className="absolute inset-0 grid place-items-center">
            <RefreshCw className={`w-5 h-5 text-[#0f1410] ${checking ? "animate-spin" : ""}`} />
          </span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          We&rsquo;ll be right back
        </h1>
        <p className="mt-3 text-[14px] leading-relaxed text-[#0f1410]/60">
          verqo is doing a quick tune-up. Your data is safe — we&rsquo;re
          reconnecting automatically and this page will refresh itself the
          moment we&rsquo;re back online.
        </p>

        <button
          data-testid="backend-down-retry"
          onClick={ping}
          disabled={checking}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0f1410] text-[#f6f6f0] px-6 h-11 text-sm font-medium hover:bg-[#0f1410]/90 disabled:opacity-60 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />
          {checking ? "Checking…" : "Try again"}
        </button>

        <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-[#0f1410]/35">
          Auto-retrying every few seconds
        </div>
      </div>
    </div>
  );
}
